import * as React from 'react'
import { PortalContainerProvider } from 'primitives/portal/portal-container'
import { useAidaDockLayout } from '../../hooks/use-aida-dock-layout'
import { resolveChatHost, useChatPortal } from '../../hooks/overlay/use-chat-portal'
import { useAidaOverlayStore } from '../../stores/overlay-store'
import { useAidaPipStore } from '../../stores/picture-in-picture-store'
import { MascotAnchorProvider } from '../layout/mascot-presence'
import { ChatParkTarget } from '../overlay/chat-park-target'
import { ParkedChat } from '../overlay/parked-chat'
import { AidaPictureInPictureShell } from './aida-picture-in-picture'

type AidaOverlayPortal = ReturnType<typeof useChatPortal> & {
  portalContainerRef: React.RefObject<HTMLDivElement | null>
}

const AidaOverlayPortalContext = React.createContext<AidaOverlayPortal | null>(null)

export function AidaChatHost({ children }: { children: React.ReactNode }) {
  useAidaDockLayout()
  const open = useAidaOverlayStore(state => state.open)
  const pipContainer = useAidaPipStore(state => state.container)
  const pipDragging = useAidaPipStore(state => state.dragging)
  const portal = useChatPortal(open)
  const portalContainerRef = React.useRef<HTMLDivElement | null>(null)

  return (
    <MascotAnchorProvider>
      <AidaOverlayPortalContext.Provider value={{ ...portal, portalContainerRef }}>
        <ChatParkTarget setPark={portal.setPark} />
        {children}
        {pipContainer ? (
          <AidaPictureInPictureShell container={pipContainer} dragging={pipDragging} />
        ) : null}
        <AidaChatPortal />
      </AidaOverlayPortalContext.Provider>
    </MascotAnchorProvider>
  )
}

export function useAidaOverlayPortal() {
  const portal = React.useContext(AidaOverlayPortalContext)
  if (!portal) {
    throw new Error('Aida overlay shells must be rendered inside AidaChatHost')
  }
  return portal
}

function AidaChatPortal() {
  const open = useAidaOverlayStore(state => state.open)
  const mode = useAidaOverlayStore(state => state.mode)
  const pipSlot = useAidaPipStore(state => state.chatSlot)
  const { keepChat, park, fullscreenSlot, detachedSlot, portalContainerRef } =
    useAidaOverlayPortal()
  const chatHost = resolveChatHost({
    open,
    mode,
    fullscreenSlot,
    detachedSlot,
    park,
    showOpenShell: false,
    pipSlot,
  })

  return keepChat && chatHost ? (
    <PortalContainerProvider containerRef={portalContainerRef}>
      <ParkedChat open={open} chatHost={chatHost} />
    </PortalContainerProvider>
  ) : null
}
